import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, User, LogOut, Settings, CheckCircle, AlertCircle, TrendingUp, Clock, X } from 'lucide-react'
import { useAuth } from '../lib/auth'
import UserSettingsModal from './UserSettingsModal'

interface HeaderProps {
  userName?: string
}

// Sample notifications - in production, these would come from an API
const sampleNotifications = [
  {
    id: '1',
    type: 'success',
    title: 'Deal Closed',
    message: 'Wealth Management Advisory deal closed for $150,000',
    time: '5 minutes ago',
    read: false,
  },
  {
    id: '2',
    type: 'warning',
    title: 'Compliance Due',
    message: 'KYC review for Global Investors LLC due in 3 days',
    time: '1 hour ago',
    read: false,
  },
  {
    id: '3',
    type: 'info',
    title: 'OKR Update',
    message: 'Q4 Revenue target reached 70% completion',
    time: '2 hours ago',
    read: false,
  },
  {
    id: '4',
    type: 'alert',
    title: 'Meeting Reminder',
    message: 'Client review meeting with Al Baraka Bank in 30 minutes',
    time: '3 hours ago',
    read: true,
  },
  {
    id: '5',
    type: 'success',
    title: 'New Lead',
    message: 'New qualified lead added to pipeline',
    time: '1 day ago',
    read: true,
  },
]

export default function Header({ userName }: HeaderProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [notifications, setNotifications] = useState(sampleNotifications)
  const notificationRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  
  const displayName = user?.full_name || userName || 'User'
  const unreadCount = notifications.filter(n => !n.read).length
  
  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
  }

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success" />
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-warning" />
      case 'info':
        return <TrendingUp className="w-5 h-5 text-info" />
      case 'alert':
        return <Clock className="w-5 h-5 text-danger" />
      default:
        return <Bell className="w-5 h-5 text-graphite-grey-600" />
    }
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="bg-white border-b border-graphite-grey-100 px-8 py-4 flex items-center justify-between shadow-sm">
      {/* Logo and Title */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <img 
            src="/logos/alliwan-arch-logo.jpg" 
            alt="Al Liwan Group"
            className="h-12 w-auto object-contain"
            onError={(e) => {
              console.error('Failed to load logo:', e)
              e.currentTarget.style.display = 'none'
            }}
          />
          <div className="border-l border-graphite-grey-200 pl-3">
            <h1 className="text-dashboard-title text-royal-blue-700">Advisory</h1>
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-6">
        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 hover:bg-pearl-white-100 rounded-lg transition-colors"
          >
            <Bell className="w-5 h-5 text-graphite-grey-600" />
            {unreadCount > 0 && (
              <>
                <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full animate-pulse"></span>
                <span className="absolute -top-1 -right-1 bg-danger text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              </>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-elevation border border-graphite-grey-100 z-50 max-h-[600px] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-4 py-3 border-b border-graphite-grey-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-graphite-grey-800">Notifications</h3>
                  <p className="text-xs text-graphite-grey-500">{unreadCount} unread</p>
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-royal-blue-600 hover:text-royal-blue-700 font-medium"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-graphite-grey-500">
                    <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 border-b border-graphite-grey-50 hover:bg-pearl-white-50 transition-colors ${
                        !notification.read ? 'bg-royal-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`text-sm font-medium ${
                              !notification.read ? 'text-graphite-grey-900' : 'text-graphite-grey-700'
                            }`}>
                              {notification.title}
                            </h4>
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="flex-shrink-0 p-1 hover:bg-graphite-grey-100 rounded transition-colors"
                              title="Delete notification"
                            >
                              <X className="w-3 h-3 text-graphite-grey-400" />
                            </button>
                          </div>
                          <p className="text-xs text-graphite-grey-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-graphite-grey-400">{notification.time}</span>
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="text-xs text-royal-blue-600 hover:text-royal-blue-700 font-medium"
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 border-t border-graphite-grey-100 bg-pearl-white-50">
                  <button className="text-sm text-royal-blue-600 hover:text-royal-blue-700 font-medium w-full text-center">
                    View all notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 px-3 py-2 hover:bg-pearl-white-100 rounded-lg transition-colors"
          >
            <div className="w-8 h-8 bg-golden-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-medium text-graphite-grey-700">{displayName}</span>
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-elevation border border-graphite-grey-100 py-2 z-50">
              <button 
                onClick={() => {
                  setShowUserMenu(false)
                  setShowSettingsModal(true)
                }}
                className="w-full px-4 py-2 text-left text-sm text-graphite-grey-700 hover:bg-pearl-white-100 flex items-center gap-3"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button 
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-danger hover:bg-danger/5 flex items-center gap-3"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      <UserSettingsModal 
        isOpen={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
      />
    </header>
  )
}
